/**
 * API クライアント
 *
 * home_karte_api との通信を抽象化する
 */

import type {
  ApiConfig,
  ApiResult,
  ApiError,
  ApiErrorResponse,
  MaintenanceErrorResponse,
  RequestOptions,
} from '../types/api';

/**
 * デバッグログをFlutter WebViewに送信
 * 開発環境でのみ動作
 */
const sendDebugLog = (message: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const webview = (window as any).DebugChannel;
      if (webview?.postMessage) {
        webview.postMessage(message);
      }
      // コンソールにも出力
      console.log('[API]', message);
    } catch {
      // WebView外での実行時は無視
    }
  }
};

/**
 * APIクライアントインターフェース
 */
export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>>;
  delete<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>>;
}

/**
 * エラータイプを判定
 */
const getErrorType = (statusCode: number): ApiError['type'] => {
  if (statusCode === 503) return 'maintenance';
  if (statusCode >= 500) return 'server';
  if (statusCode >= 400) return 'client';
  return 'unknown';
};

/**
 * レスポンスをパースしてエラーを作成
 */
const parseErrorResponse = async (
  response: Response
): Promise<ApiError> => {
  const statusCode = response.status;
  const errorType = getErrorType(statusCode);

  try {
    const body = await response.json();

    if (errorType === 'maintenance') {
      return {
        type: 'maintenance',
        statusCode,
        message: 'Service is under maintenance',
        maintenance: body as MaintenanceErrorResponse,
      };
    }

    const errorResponse = body as ApiErrorResponse;
    return {
      type: errorType,
      statusCode,
      message: errorResponse.errors?.[0]?.errorMessage || 'Unknown error',
      errors: errorResponse.errors,
    };
  } catch {
    return {
      type: errorType,
      statusCode,
      message: `HTTP ${statusCode} error`,
    };
  }
};

/**
 * APIクライアントを作成
 */
export const createApiClient = (config: ApiConfig): ApiClient => {
  const { baseUrl, headers: configHeaders = {} } = config;

  const request = async <T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResult<T>> => {
    const url = `${baseUrl}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...configHeaders,
      ...options?.headers,
    };

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      sendDebugLog(`${method} ${url} - リクエスト開始`);
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        const error = await parseErrorResponse(response);
        sendDebugLog(`${method} ${url} - エラー: ${response.status} ${error.message}`);
        return { success: false, error };
      }

      const data = await response.json() as T;
      // レスポンスボディをログ出力（長すぎる場合は切り詰め）
      const bodyStr = JSON.stringify(data);
      const truncatedBody = bodyStr.length > 500 ? bodyStr.substring(0, 500) + '...' : bodyStr;
      sendDebugLog(`${method} ${url} - 成功: ${response.status}, body=${truncatedBody}`);
      return { success: true, data };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error';
      sendDebugLog(`${method} ${url} - ネットワークエラー: ${errorMessage}`);
      const error: ApiError = {
        type: 'network',
        message: errorMessage,
      };
      return { success: false, error };
    }
  };

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('POST', path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>('PUT', path, body, options),
    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>('DELETE', path, undefined, options),
  };
};
